import { DiffLine, DiffRow, lineDiff } from "../utils/lineDiff";
import { DiffView, Icons, TextButton } from "../../../ds";
import { useMemo, useState } from "react";

interface IContentDiffProps {
    before: string;
    after: string;
    /** For the accessible name, e.g. "Content" or "Rules". */
    label: string;
    /** What the text is, for the head bar ("AstroMark", "JSON"). */
    format?: string;
}

/** Every row, folds opened. */
function unfolded(lines: DiffLine[]): DiffRow[] {
    return lines.flatMap((line) => (line.type === "fold" ? line.rows : [line]));
}

/** Lines of the text the diff ends on (the "after"; the "before" when everything was removed). */
function lineCount(lines: DiffLine[]): number {
    let newest = 0;
    let oldest = 0;
    for (const row of unfolded(lines)) {
        newest = Math.max(newest, row.newNo ?? 0);
        oldest = Math.max(oldest, row.oldNo ?? 0);
    }
    return newest || oldest;
}

/**
 * Unified line diff of two bodies (the change requests' model, `lineDiff`: word highlights inside replaced lines, long unchanged
 * runs folded) drawn by the design system's `DiffView`, under a head bar with what the text is, how long, `+n −n`, and
 * "Expand all" when something is folded.
 */
export function ContentDiff(props: IContentDiffProps) {
    const diff = useMemo(() => lineDiff(props.before, props.after), [props.before, props.after]);
    const [all, setAll] = useState(false);
    const hasFolds = useMemo(() => diff.lines.some((line) => line.type === "fold"), [diff]);
    const shown = useMemo(() => (all ? unfolded(diff.lines) : diff.lines), [all, diff]);

    if (diff.added === 0 && diff.removed === 0) {
        return <div className="cr-diff-empty">No changes to the {props.label.toLowerCase()}.</div>;
    }

    const count = lineCount(diff.lines);
    return (
        <div className="cr-diff">
            <div className="cr-diff-head">
                <span className="cr-diff-caption">
                    {props.format ?? "AstroMark"} · {count === 1 ? "1 line" : `${count} lines`}
                </span>
                <span className="cr-diff-stat is-add">+{diff.added}</span>
                <span className="cr-diff-stat is-del">−{diff.removed}</span>
                {hasFolds ? (
                    <TextButton type="white" className="cr-diff-expand" text={all ? "Fold unchanged" : "Expand all"}
                        icon={<Icons.chevronDown size={14} />} onClick={() => setAll((v) => !v)} />
                ) : null}
            </div>
            <DiffView key={all ? "all" : "folded"} lines={shown} label={props.label} />
        </div>
    );
}
